import { Button, Header, StickyFooterButtons, useLoading } from "best-common-react";
import React, { useEffect, useMemo, useState } from "react";
import { getEvent, getVenueAcceptedBillings, saveRequest } from "../../api/RequesTixApi";
import RequestInformation from "../../components/requests/form/RequestInformation";
import TicketRequestForm from "../../components/requests/form/TicketRequestForm";
import TicketAgreement from "../../components/tickets/TicketAgreement";
import RouteConstants from "../../constants/RouteConstants";
import { TicketRequestAllotmentTypes } from "../../constants/TicketRequestConstants";
import { useAuth } from "../../contexts/AuthContext";
import { TicketRequestProvider } from "../../contexts/TicketRequestContext";
import { UserAllotmentCountDTO } from "../../types/Allotment";
import { GameDTO } from "../../types/Game";
import { SeasonTypeCode } from "../../types/Season";
import { FlattenedTicketRequest, SaveTicketRequestDTO } from "../../types/TicketRequest";
import { useAllotmentAmounts } from "../../util/AllotmentUtil";
import { getSeasonTypeForGame } from "../../util/SeasonUtil";
import { capitalizeFirstLetter } from "../../util/StringUtil";
import { isBusiness, isCompOnly, useRequestValidation, useValidCreditCard } from "../../util/TicketRequestUtil";
import { useLocation, useNavigate, useParams } from "react-router";
import { RequestTypeDesc } from "../../types/RequestType";
import { VenueAcceptedBilling } from "../../types/Venue";

type TicketRequestCompParams = {
  type: RequestTypeDesc;
  gamePk: string;
};

const TicketRequestComp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { userInfo, isAdmin } = useAuth();
  const { gamePk: gamePkParam, type } = useParams<TicketRequestCompParams>();
  const gamePk = parseInt(gamePkParam!);
  const [ticketRequest, setTicketRequest] = useState<FlattenedTicketRequest>({
    // @ts-ignore
    type: type,
    //@ts-ignore
    submitter: userInfo,
    //@ts-ignore
    requester: userInfo,
    notifyRequester: false,
    // @ts-ignore
    originalGamePk: null,
    gamePk: gamePk,
    projectId: -1,
    businessId: null,
    // @ts-ignore
    requestType: undefined,
    // @ts-ignore
    quantity: undefined,
    //@ts-ignore
    approvingSupervisor: {
      // @ts-ignore
      employeeUserId: null,
    },
    maxPriceOptionId: null,
    userBilling: null,
    recipient: "",
    recipientFirst: "",
    recipientLast: "",
    deliveryEmail: "",
    forGovernmentUse: false,
    forCharityUse: false,
    comments: "",
    surroundingGames: [],
    phoneNumber: "",
  });

  const [accessible, setAccessible] = useState<boolean>(false);
  const [canSave, setCanSave] = useState<boolean>(false);
  const [allotment, setAllotment] = useState<UserAllotmentCountDTO | undefined>(undefined);
  const [allotments, setAllotments] = useState<UserAllotmentCountDTO[]>([]);
  const [game, setGame] = useState<GameDTO | undefined>(undefined);
  const [hasBusinessAllotment, setHasBusinessAllotment] = useState<boolean>(false);
  const [hasPersonalAllotment, setHasPersonalAllotment] = useState<boolean>(false);
  const [venueBillings, setVenueBillings] = useState<VenueAcceptedBilling[]>([]);
  const [agreed, setAgreed] = useState<boolean>(false);

  const isFormValid = useRequestValidation(ticketRequest, allotment, game?.allowSmsTickets);
  const isCardValid = useValidCreditCard(ticketRequest, venueBillings, game?.venue);
  const isBusinessType = useMemo(() => isBusiness(type), [type]);

  const save = async () => {
    try {
      setLoading(true);
      const { submitter, requester, approvingSupervisor, ...rest } = ticketRequest;
      //@ts-ignore
      const toSave: SaveTicketRequestDTO = {
        // @ts-ignore
        submitterId: submitter.employeeUserId,
        // @ts-ignore
        requesterId: requester.employeeUserId,
        approvingSupervisorId: approvingSupervisor.employeeUserId,
        ...rest,
      };
      await saveRequest(toSave);
      cancel();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => {
    const url = !!location?.state?.previousLocation ? location.state.previousLocation : RouteConstants.BASE;
    navigate(url);
  };

  useEffect(() => {
    if (!!game) {
      const season: SeasonTypeCode = getSeasonTypeForGame(game);
      const allot: UserAllotmentCountDTO | undefined = allotments.find((allotment: UserAllotmentCountDTO) => {
        return (
          allotment.season?.seasonType?.seasonTypeId === season.id &&
          allotment.season.year.toString() === game.season.toString() &&
          (isBusiness(type)
            ? allotment.allotmentType.allotmentTypeId === TicketRequestAllotmentTypes.BUSINESS
            : allotment.allotmentType.allotmentTypeId === TicketRequestAllotmentTypes.PERSONAL)
        );
      });
      setAllotment(allot);
    }
  }, [allotments, game, type]);

  useEffect(() => {
    getEvent(gamePk).then((data: GameDTO) => {
      setGame(data);
    });
  }, [gamePk]);

  useEffect(() => {
    if (game?.venue?.id) {
      getVenueAcceptedBillings(game.venue.id).then((data) => {
        setVenueBillings(data);
      });
    }
  }, [game]);

  useEffect(() => {
    if (!!game) {
      const { hasBusinessAllotment, hasPersonalAllotment } = useAllotmentAmounts(
        allotments,
        game.season,
        game.gameType,
      );
      setHasBusinessAllotment(hasBusinessAllotment);
      setHasPersonalAllotment(hasPersonalAllotment);
    }
  }, [game, allotments]);

  useEffect(() => {
    const hasAllotmentLeft = !isCompOnly(ticketRequest) || isBusinessType ? hasBusinessAllotment : hasPersonalAllotment;
    const hasAgreed = isBusinessType ? true : agreed;
    setCanSave(isFormValid && hasAgreed && isCardValid && ((accessible && hasAllotmentLeft) || isAdmin));
  }, [ticketRequest, isFormValid, allotments, isCardValid, agreed, isBusiness]);

  return (
    <TicketRequestProvider gamePk={gamePkParam}>
      <div>
        <Header title={`${capitalizeFirstLetter(type)} Request`}>
          <StickyFooterButtons startsOpen={true}>
            <Button variant="primary" disabled={!canSave} onClick={save}>
              Submit
            </Button>
            <Button variant="default" onClick={cancel}>
              Cancel
            </Button>
          </StickyFooterButtons>
        </Header>
        <div className="d-flex flex-md-nowrap flex-wrap bcr-p-2">
          <RequestInformation
            originalGamePk={ticketRequest.originalGamePk}
            setAccessible={setAccessible}
            userId={ticketRequest.requester.employeeUserId}
            setAllotments={setAllotments}
            allotments={allotments}
            type={type!}
          />
          <div>
            <TicketRequestForm
              ticketRequest={ticketRequest}
              setTicketRequest={setTicketRequest}
              allotment={allotment}
              phoneNumberRequired={game?.allowSmsTickets}
            />
            {!isBusinessType && <TicketAgreement setAgreed={setAgreed} />}
          </div>
        </div>
      </div>
    </TicketRequestProvider>
  );
};

export default TicketRequestComp;
